import React, { useState, useEffect, useRef } from 'react';
import { getUserMatchBets, Match } from '../../api/matchesApi';
import { format } from 'date-fns';
import { ShieldCheckIcon } from '@heroicons/react/24/outline';
import { Target } from 'lucide-react';
import Layout from '../../components/Layout';
import MatchCard from '../../components/MatchCard';
import { formatRound } from '../../utils/general';
import { usePlaceMatchBet } from '../../hooks/usePlaceMatchBet';

// Height offset for the sticky header / menu bar to avoid hiding it on auto-scroll
const HEADER_OFFSET = 80;

// Extended match interface
interface ExtendedMatch extends Match {
  knockout?: boolean;
}

const Matches: React.FC = () => {
  const [matches, setMatches] = useState<ExtendedMatch[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [betScores, setBetScores] = useState<Record<string, { home: string, away: string }>>({});
  const [qualifiers, setQualifiers] = useState<Record<string, string>>({});
  const matchRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [autoScrolled, setAutoScrolled] = useState(false);

  // Use shared hook for placing bets
  const { placeBet } = usePlaceMatchBet({
    onSuccess: (matchId, updatedMatch) => {
      // Update the match in the local state to show the bet
      setMatches(prevMatches => 
        prevMatches.map(match => {
          if (match.id === matchId) {
            return {
              ...match,
              user_bet_home_score: updatedMatch.user_bet_home_score,
              user_bet_away_score: updatedMatch.user_bet_away_score,
              user_bet_winner: updatedMatch.user_bet_winner
            };
          }
          return match;
        })
      );
    },
    onError: (error) => {
      console.error('Error placing/updating bet:', error);
      alert('אירעה שגיאה בשמירה. אנא נסה שוב מאוחר יותר.');
    }
  });

  useEffect(() => {
    const fetchMatches = async () => {
      try {
        setLoading(true);
        const matchData = await getUserMatchBets();
        // Convert to ExtendedMatch type with additional properties
        const extendedMatches = Array.isArray(matchData) 
          ? matchData.map(match => ({ ...match, knockout: false })) 
          : [];
        setMatches(extendedMatches);
        setError(null);
      } catch (err) {
        setError('Failed to load matches. Please try again later.');
        console.error('Error fetching matches:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchMatches();
  }, []);

  // Auto-scroll to first live or last completed match after matches are loaded
  useEffect(() => {
    if (loading || autoScrolled || matches.length === 0) return;
    let targetId: string | null = null;
    // Find first live match
    for (const match of matches) {
      if (match.status === 'LIVE') {
        targetId = match.id;
        break;
      }
    }
    // If no live match, find last completed
    if (!targetId) {
      for (let i = matches.length - 1; i >= 0; i--) {
        if (matches[i].status === 'COMPLETED') {
          targetId = matches[i].id;
          break;
        }
      }
    }
    // Scroll to the target match with header offset to keep the menu visible
    const el = targetId ? matchRefs.current[targetId] : null;
    if (el) {
      // First scroll the element into view at the start of the viewport
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      // Then nudge up by the header offset to keep the menu visible
      setTimeout(() => {
        window.scrollBy({ top: -HEADER_OFFSET, behavior: 'smooth' });
      }, 150);
      setAutoScrolled(true);
    }
  }, [loading, matches, autoScrolled]);

  // Group matches by round/league
  const matchesByRound: Record<string, ExtendedMatch[]> = React.useMemo(() => {
    if (!Array.isArray(matches)) {
      console.error('Matches is not an array:', matches);
      return {};
    }

    return matches.reduce((acc, match) => {
      const round = match.round || 'Unknown';
      if (!acc[round]) {
        acc[round] = [];
      }
      acc[round].push(match);
      return acc;
    }, {} as Record<string, ExtendedMatch[]>);
  }, [matches]);

  // Wrapper to get match from matches array for the hook
  const handlePlaceBet = async (matchId: string, homeScore: string, awayScore: string, winnerId?: number | null) => {
    const match = matches.find(m => m.id === matchId);
    await placeBet(matchId, homeScore, awayScore, winnerId, match);
  };

  return (
    <Layout>
      <div>
        <h1 className="text-2xl font-bold text-white mb-6">כל המשחקים</h1>
        
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-stake-blue"></div>
          </div>
        ) : error ? (
          <div className="bg-red-500 text-white p-4 rounded-md">
            {error}
          </div>
        ) : matches.length === 0 ? (
          <div className="bg-dark-200 p-6 rounded-md text-gray-300 text-center">
            <p>אין משחקים כרגע</p>
          </div>
        ) : (
          <div>
            {Object.entries(matchesByRound).map(([round, roundMatches]) => (
              <div key={round} className="mb-8">
                <h2 className="max-w-lg mx-auto text-xl font-semibold text-white mb-4">{formatRound(round)}</h2>
                <div className="space-y-4">
                  {roundMatches.map(match => (
                    <div
                      key={match.id}
                      ref={el => (matchRefs.current[match.id] = el)}
                    >
                      <MatchCard 
                        match={match} 
                        onPlaceBet={handlePlaceBet}
                        compact={true}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Matches; 